import type { DrillMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import { contentQueries } from "@-/dev.improve.haniverse.database.schemas.content.query";
import { staticQueries } from "@-/dev.improve.haniverse.database.schemas.static.query";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { hasLength, hasMinLength } from "@-/com.npmjs.ts-array-length";

export type FrontCardSliceLookup = { front: string } | { id: number };

type GraphemeRow = {
  glyph: string;
  strokeCount: number | null;
  strokeData: { medians: [number, number][]; path: string }[] | null;
};

type PronunciationCardRow = {
  gloss: string;
  pinyin: string;
};

// A meaning card with its pronunciation cards, fetched in one relational query.
type MeaningCardWithPronunciations = {
  back: string | null;
  grapheme: string;
  id: number;
  pronunciationCards: PronunciationCardRow[];
};

function coerceMedianPolyline(
  median: [number, number][] | null | undefined,
): NonNullable<DrillMorphemeSlice["graphemeStrokes"]>[number]["median"] {
  if (median === null || median === undefined) {
    return [];
  }
  const out: NonNullable<DrillMorphemeSlice["graphemeStrokes"]>[number]["median"] = [];
  for (const tuple of median) {
    if (!Array.isArray(tuple) || !hasMinLength(tuple, 2)) {
      continue;
    }
    const x = Math.trunc(Number(tuple[0]));
    const y = Math.trunc(Number(tuple[1]));
    if (Number.isFinite(x) && Number.isFinite(y)) {
      out.push([x, y]);
    }
  }
  return out;
}

/** `null` when any glyph of the front has no stroke data (see `graphemeStrokeListSchema`). */
function buildGraphemeStrokes(
  graphemesForFront: GraphemeRow[],
): DrillMorphemeSlice["graphemeStrokes"] {
  const out: NonNullable<DrillMorphemeSlice["graphemeStrokes"]> = [];
  let strokeIndex = 0;
  for (const grapheme of graphemesForFront) {
    if (grapheme.strokeData === null) {
      return null;
    }
    for (const { medians, path } of grapheme.strokeData) {
      out.push({
        index: strokeIndex,
        median: coerceMedianPolyline(medians),
        path,
      });
      strokeIndex += 1;
    }
  }
  return out;
}

function buildFrontCard(
  card: MeaningCardWithPronunciations,
  graphemesForFront: GraphemeRow[],
): DrillMorphemeSlice {
  return {
    back: card.back ?? undefined,
    graphemes: graphemesForFront.map((g) => ({
      grapheme: g.glyph,
      strokeCount: g.strokeCount,
    })),
    graphemeStrokes: buildGraphemeStrokes(graphemesForFront),
    pinyinList: card.pronunciationCards.map((p) => ({
      gloss: p.gloss,
      pinyin: p.pinyin,
    })),
  };
}

export function useGetDrillMorphemeSliceQueryKey() {
  const getMeaningLookupQueryKey = contentQueries(
    import.meta.location,
  ).appGraphemeMeaningCard.findMany.useGetQueryKey({});
  const getPronunciationLookupQueryKey = contentQueries(
    import.meta.location,
  ).appGraphemePronunciationCard.findMany.useGetQueryKey({});
  const getMeaningWithPronunciationsQueryKey = contentQueries(
    import.meta.location,
  ).appGraphemeMeaningCard.findMany.useGetQueryKey({
    with: { pronunciationCards: true },
  });
  const getGraphemeWithStrokeDataQueryKey = staticQueries(
    import.meta.location,
  ).grapheme.findMany.useGetQueryKey({
    with: { strokeData: true },
  });

  return (lookup: FrontCardSliceLookup) => {
    const deps = {
      getGraphemeWithStrokeDataQueryKey,
      getMeaningLookupQueryKey,
      getMeaningWithPronunciationsQueryKey,
      getPronunciationLookupQueryKey,
    };

    if ("id" in lookup) {
      return ["drillMorpheme", "id", lookup.id, deps] as const;
    } else {
      return ["drillMorpheme", "front", lookup.front, deps] as const;
    }
  };
}

export const [useDrillMorphemeSliceQueryOptions, drillMorphemeSliceQueryFn] = createUseQueryOptions(
  useGetDrillMorphemeSliceQueryKey,
  async ({ client, queryKey }) => {
    const {
      getGraphemeWithStrokeDataQueryKey,
      getMeaningLookupQueryKey,
      getMeaningWithPronunciationsQueryKey,
      getPronunciationLookupQueryKey,
    } = queryKey[3];
    const lookup: FrontCardSliceLookup =
      queryKey[1] === "id" ? { id: queryKey[2] } : { front: queryKey[2] };
    const lookups = [lookup];
    if (hasLength(lookups, 0)) {
      return null;
    }

    const requestedFronts = uniq(lookups.flatMap((l) => ("front" in l ? [l.front] : [])));
    const requestedIds = uniq(lookups.flatMap((l) => ("id" in l ? [l.id] : [])));

    let lookupGraphemeRows: { cardId: number; grapheme: string }[];
    if (hasLength(requestedIds, 0)) {
      lookupGraphemeRows = [];
    } else {
      const [meaningLookupRows, pronunciationLookupRows] = await Promise.all([
        client.fetchQuery(
          contentQueries(import.meta.location).appGraphemeMeaningCard.findMany.getQueryOptions(
            getMeaningLookupQueryKey,
            {
              where: { id: { in: requestedIds } },
            },
          ),
        ),
        client.fetchQuery(
          contentQueries(
            import.meta.location,
          ).appGraphemePronunciationCard.findMany.getQueryOptions(getPronunciationLookupQueryKey, {
            where: { id: { in: requestedIds } },
          }),
        ),
      ]);
      lookupGraphemeRows = [...meaningLookupRows, ...pronunciationLookupRows].map((r) => ({
        cardId: r.id,
        grapheme: r.grapheme,
      }));
    }
    const frontByLookupId = new Map(lookupGraphemeRows.map((r) => [r.cardId, r.grapheme]));

    const allFrontsToFetch = uniq([
      ...requestedFronts,
      ...lookupGraphemeRows.map((r) => r.grapheme),
    ]);

    const meaningCardRows: MeaningCardWithPronunciations[] = hasLength(allFrontsToFetch, 0)
      ? []
      : await client
          .fetchQuery(
            contentQueries(import.meta.location).appGraphemeMeaningCard.findMany.getQueryOptions(
              getMeaningWithPronunciationsQueryKey,
              { where: { grapheme: { in: allFrontsToFetch } } },
            ),
          )
          .then((rows) =>
            rows.map((row) => ({
              back: row.gloss,
              grapheme: row.grapheme,
              id: row.id,
              pronunciationCards: row.pronunciationCards,
            })),
          );

    const cardByFront = new Map(meaningCardRows.map((c) => [c.grapheme, c]));
    const allFronts = uniq(meaningCardRows.map((c) => c.grapheme));

    const graphemeRows = hasLength(allFronts, 0)
      ? []
      : await client.fetchQuery(
          staticQueries(import.meta.location).grapheme.findMany.getQueryOptions(
            getGraphemeWithStrokeDataQueryKey,
            {
              where: { glyph: { in: allFronts } },
            },
          ),
        );

    const graphemesByGlyph = new Map<string, GraphemeRow[]>();
    for (const g of graphemeRows) {
      const list = graphemesByGlyph.get(g.glyph) ?? [];
      list.push({
        glyph: g.glyph,
        strokeCount: g.strokeCount,
        strokeData: g.strokeData?.strokeData ?? null,
      });
      graphemesByGlyph.set(g.glyph, list);
    }

    const cards = lookups.map((lookupItem) => {
      let card: MeaningCardWithPronunciations | undefined;
      if ("front" in lookupItem) {
        card = cardByFront.get(lookupItem.front);
      } else {
        const front = frontByLookupId.get(lookupItem.id);
        if (front !== undefined) {
          card = cardByFront.get(front);
        }
      }
      if (card === undefined) {
        return null;
      }
      const graphemesForFront = graphemesByGlyph.get(card.grapheme);
      if (graphemesForFront === undefined || hasLength(graphemesForFront, 0)) {
        return null;
      }
      return buildFrontCard(card, graphemesForFront);
    });
    return cards[0];
  },
);
